import { generate } from '@ant-design/colors'
import { FastColor } from '@ant-design/fast-color'

import { darkPalette, lightPalette, palettes } from './palette'
import type { ThemeMode } from './palette'

const AA_TEXT = 4.5
const AAA_TEXT = 7
const LIGHTNESS_STEP = 0.02
const MIX_STEP = 2
const PRIMARY_BG_SHARE = 12
const TINT_SHARE = 16
const TINT_BORDER_SHARE = 28
const NEUTRAL_BORDER_SHARE = 18
const MIN_TINT_CONTRAST = 1.08
const MIN_BORDER_CONTRAST = 1.2

const lightTextSurfaces = [
  lightPalette.container,
  lightPalette.layout,
  lightPalette.subtle,
  lightPalette.hover,
  lightPalette.tag
]

export const contrastRatio = (foreground: string, background: string) => {
  const [lighter, darker] = [
    new FastColor(foreground).getLuminance(),
    new FastColor(background).getLuminance()
  ].sort((a, b) => b - a)

  return (lighter + 0.05) / (darker + 0.05)
}

export const darkPrimaryOf = (seed: string) =>
  generate(seed, { theme: 'dark' })[5]

export const primaryBgOf = (color: string) =>
  new FastColor(lightPalette.container)
    .mix(color, PRIMARY_BG_SHARE)
    .toHexString()

const readsOnDark = (seed: string) =>
  contrastRatio(darkPrimaryOf(seed), darkPalette.elevated) >= AA_TEXT

const liftPrimaryForDark = (seed: string) => {
  if (readsOnDark(seed)) return seed

  const { h, s, l } = new FastColor(seed).toHsl()

  for (let lightness = l; lightness <= 1; lightness += LIGHTNESS_STEP) {
    const candidate = new FastColor({ h, s, l: lightness }).toHexString()

    if (readsOnDark(candidate)) return candidate
  }

  return '#FFFFFF'
}

const readsOnLight = (color: string) =>
  [...lightTextSurfaces, primaryBgOf(color)].every(
    (surface) => contrastRatio(color, surface) >= AA_TEXT
  )

const mixTowardNeutralUntil = (
  color: string,
  neutral: string,
  isReadable: (candidate: string) => boolean
) => {
  for (let share = MIX_STEP; share < 100; share += MIX_STEP) {
    const candidate = new FastColor(color).mix(neutral, share).toHexString()

    if (isReadable(candidate)) return candidate
  }

  return neutral
}

const deepenPrimaryForLight = (seed: string) =>
  readsOnLight(seed)
    ? seed
    : mixTowardNeutralUntil(seed, lightPalette.text, readsOnLight)

const memoizeByColor = <Resolved>(resolve: (color: string) => Resolved) => {
  const cache = new Map<string, Resolved>()

  return (color: string) => {
    const cached = cache.get(color)

    if (cached) return cached

    const resolved = resolve(color)

    cache.set(color, resolved)

    return resolved
  }
}

export const readablePrimaryOnDark = memoizeByColor(liftPrimaryForDark)

export const readablePrimaryOnLight = memoizeByColor(deepenPrimaryForLight)

const neutralTextOn = (base: string) => {
  const [onFill, oppositeNeutral] =
    contrastRatio(lightPalette.textOnPrimary, base) >=
    contrastRatio(darkPalette.textOnPrimary, base)
      ? [lightPalette.textOnPrimary, darkPalette.textOnPrimary]
      : [darkPalette.textOnPrimary, lightPalette.textOnPrimary]
  const readsUnderText = (shade: string) =>
    contrastRatio(onFill, shade) >= AA_TEXT

  return {
    onFill,
    readableShade: (shade: string) =>
      readsUnderText(shade)
        ? shade
        : mixTowardNeutralUntil(shade, oppositeNeutral, readsUnderText)
  }
}

type FillShades = { base: string; hover: string; press: string }

export const readableFill = ({ base, hover, press }: FillShades) => {
  const { onFill, readableShade } = neutralTextOn(base)

  return {
    onFill,
    base: readableShade(base),
    hover: readableShade(hover),
    press: readableShade(press)
  }
}

const liftLightnessUntil = (
  color: string,
  isReadable: (candidate: string) => boolean,
  fallback: string
) => {
  const { h, s, l } = new FastColor(color).toHsl()

  for (let lightness = l; lightness <= 1; lightness += LIGHTNESS_STEP) {
    const candidate = new FastColor({ h, s, l: lightness }).toHexString()

    if (isReadable(candidate)) return candidate
  }

  return fallback
}

type Tint = { background: string; text: string; border: string }

const tintOn =
  (mode: ThemeMode) =>
  (color: string): Tint => {
    const palette = palettes[mode]
    const mixIntoSurface = (share: number, source = color) =>
      new FastColor(palette.container).mix(source, share).toHexString()
    const tinted = mixIntoSurface(TINT_SHARE)
    const background =
      contrastRatio(tinted, palette.container) < MIN_TINT_CONTRAST
        ? palette.tag
        : tinted
    const tintedBorder = mixIntoSurface(TINT_BORDER_SHARE)
    const border =
      contrastRatio(tintedBorder, palette.container) < MIN_BORDER_CONTRAST
        ? mixIntoSurface(NEUTRAL_BORDER_SHARE, palette.text)
        : tintedBorder
    const readsOnBackground = (candidate: string) =>
      contrastRatio(candidate, background) >= AAA_TEXT

    if (readsOnBackground(color)) return { background, text: color, border }

    const text =
      mode === 'dark'
        ? liftLightnessUntil(color, readsOnBackground, palette.text)
        : mixTowardNeutralUntil(color, palette.text, readsOnBackground)

    return { background, text, border }
  }

const tints: Record<ThemeMode, (color: string) => Tint> = {
  light: memoizeByColor(tintOn('light')),
  dark: memoizeByColor(tintOn('dark'))
}

export const readableTint = (color: string, mode: ThemeMode) =>
  tints[mode](color)
